import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../hooks/useWebSocket";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import ProductCustomizationModal from "../../components/ProductCustomizationModal";
import { 
  ShoppingCart, Plus, Minus, Search, Trash2, Edit, 
  User, LogOut, RefreshCw, Bell, Send, Check
} from "lucide-react";
import { cn, formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CashierOrders = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Order state
  const [orderType, setOrderType] = useState("eat_in");
  const [tableNumber, setTableNumber] = useState("");
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Edit order state
  const [editingOrder, setEditingOrder] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  
  // Orders list
  const [recentOrders, setRecentOrders] = useState([]);

  // Customization modal
  const [customizeItem, setCustomizeItem] = useState(null);

  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === "new_order" || message.type === "order_status_update") {
      fetchRecentOrders();
    }
  }, []);

  useWebSocket(user?.restaurant_id, handleWebSocketMessage);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resResponse, catsResponse, itemsResponse] = await Promise.all([
        axios.get(`${API}/restaurant/profile`),
        axios.get(`${API}/restaurant/categories`),
        axios.get(`${API}/restaurant/items`),
      ]);
      setRestaurant(resResponse.data);
      setCategories(catsResponse.data);
      setMenuItems(itemsResponse.data);
      if (catsResponse.data.length > 0) {
        setActiveCategory(catsResponse.data[0].id);
      }
      await fetchRecentOrders();
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      const recent = response.data.filter(o => 
        ["new", "preparing", "ready"].includes(o.status)
      ).slice(0, 10);
      setRecentOrders(recent);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  };

  const addToCart = (item) => {
    // If item has variants OR extras → open customization modal first
    if ((item.variants && item.variants.length > 0) || (item.extras && item.extras.length > 0)) {
      setCustomizeItem(item);
      return;
    }
    // Otherwise quick-add as a customized line with no extras
    addLineToCart({
      id: `${item.id}-${Date.now()}`,
      menu_item_id: item.id,
      name: item.name,
      price: item.price,
      base_price: item.price,
      quantity: 1,
      variant_id: null,
      variant_name: null,
      selected_extras: [],
      extras_details: [],
      notes: "",
      category_type: item.category_type || "food",
    });
  };

  const addLineToCart = (line) => {
    setCart(prev => [...prev, line]);
    toast.success(`${line.name} added`, { duration: 1000 });
  };

  const updateCartQuantity = (cartItemId, delta) => {
    setCart(cart.map(item => {
      if (item.id === cartItemId) {
        const newQty = Math.max(0, item.quantity + delta);
        return newQty === 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.id !== cartItemId));
  };

  const clearCart = () => {
    setCart([]);
    setTableNumber("");
  };

  const getCartTotal = () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const submitOrder = async (paymentMethod = "pay_at_cashier") => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (orderType === "eat_in" && !tableNumber && restaurant?.enable_table_number) {
      toast.error("Please enter table number");
      return;
    }

    try {
      const orderData = {
        order_type: orderType,
        table_number: orderType === "eat_in" ? tableNumber : null,
        items: cart.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          variant_id: item.variant_id || null,
          selected_extras: item.selected_extras || [],
          notes: item.notes || "",
        })),
        payment_method: paymentMethod,
        customer_phone: null,
      };

      await axios.post(`${API}/cashier/orders`, orderData);
      toast.success("Order sent to kitchen!");
      clearCart();
      fetchRecentOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    }
  };

  const openEditOrder = (order) => {
    setEditingOrder({
      ...order,
      items: order.items.map(item => ({
        ...item,
        id: item.id || `${item.menu_item_id}-${Date.now()}`,
      }))
    });
    setEditDialog(true);
  };

  const updateEditingOrderItem = (itemId, delta) => {
    if (!editingOrder) return;
    setEditingOrder({
      ...editingOrder,
      items: editingOrder.items.map(item => {
        if (item.id === itemId) {
          const newQty = Math.max(0, item.quantity + delta);
          return newQty === 0 ? null : { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean)
    });
  };

  const addItemToEditingOrder = (menuItem) => {
    if (!editingOrder) return;
    const existing = editingOrder.items.find(i => i.menu_item_id === menuItem.id);
    if (existing) {
      updateEditingOrderItem(existing.id, 1);
    } else {
      setEditingOrder({
        ...editingOrder,
        items: [...editingOrder.items, {
          id: `new-${Date.now()}`,
          menu_item_id: menuItem.id,
          name: menuItem.name,
          unit_price: menuItem.price,
          quantity: 1,
          total: menuItem.price,
          notes: "",
        }]
      });
    }
  };

  const saveEditedOrder = async () => {
    if (!editingOrder) return;
    try {
      await axios.put(`${API}/orders/${editingOrder.id}/items`, {
        items: editingOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          variant_id: item.variant_id || null,
          notes: item.notes || "",
        }))
      });
      toast.success("Order updated! Kitchen notified.");
      setEditDialog(false);
      setEditingOrder(null);
      fetchRecentOrders();
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = !activeCategory || item.category_id === activeCategory;
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && item.is_available;
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="cashier-orders">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {restaurant?.logo_data ? (
              <img 
                src={restaurant.logo_data} 
                alt={restaurant.name}
                className="w-10 h-10 rounded-xl object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h1 className="font-bold">{restaurant?.name}</h1>
              <p className="text-xs text-muted-foreground">Take Order</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/cashier")}>
              Payments
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Left - Menu */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search & Categories */}
          <div className="bg-white border-b p-3 space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveCategory(cat.id)}
                  className="whitespace-nowrap"
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Menu Grid — big visual touch-friendly cards */}
          <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
            {filteredItems.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">No items in this category</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredItems.map((item) => {
                  const unavailable = item.is_available === false;
                  const customizable = (item.variants?.length > 0) || (item.extras?.length > 0);
                  const inCartCount = cart.filter(c => c.menu_item_id === item.id).reduce((n, c) => n + c.quantity, 0);
                  return (
                    <button key={item.id}
                      onClick={() => !unavailable && addToCart(item)}
                      disabled={unavailable}
                      data-testid={`cashier-menu-${item.id}`}
                      className={cn(
                        "bg-white rounded-xl overflow-hidden text-left border border-slate-200 shadow-sm transition-all hover:shadow-md hover:border-slate-300",
                        unavailable ? "opacity-50 grayscale cursor-not-allowed" : "active:scale-95"
                      )}>
                      <div className="aspect-square bg-slate-100 relative overflow-hidden">
                        {(item.image_data || item.image_url) ? (
                          <img src={item.image_data || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <ShoppingCart className="w-10 h-10" />
                          </div>
                        )}
                        {inCartCount > 0 && (
                          <div className="absolute top-2 right-2 bg-emerald-500 text-white min-w-[28px] h-7 rounded-full flex items-center justify-center font-bold text-sm shadow px-2">
                            {inCartCount}
                          </div>
                        )}
                        {customizable && !unavailable && (
                          <div className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold bg-slate-900/80 text-white px-1.5 py-0.5 rounded">
                            Customize
                          </div>
                        )}
                        {unavailable && (
                          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                            <span className="text-[11px] font-bold bg-red-600 text-white px-2 py-1 rounded-full">UNAVAILABLE</span>
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <h4 className="font-semibold text-sm text-slate-900 truncate">{item.name}</h4>
                        <p className="text-primary font-bold text-sm mt-0.5">{formatCurrency(item.price)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right - Cart & Recent Orders */}
        <div className="w-80 lg:w-96 bg-white border-l flex flex-col">
          {/* Order Type */}
          <div className="p-3 border-b">
            <Tabs value={orderType} onValueChange={setOrderType}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="eat_in">Dine In</TabsTrigger>
                <TabsTrigger value="takeaway">Takeaway</TabsTrigger>
              </TabsList>
            </Tabs>
            {orderType === "eat_in" && restaurant?.enable_table_number && (
              <Input
                placeholder="Table #"
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                className="mt-2"
                data-testid="cashier-table-input"
              />
            )}
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-3">
            {cart.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Tap items to add</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {item.name}
                        {item.variant_name && <span className="text-slate-400 text-xs"> · {item.variant_name}</span>}
                      </p>
                      {item.extras_details?.length > 0 && (
                        <p className="text-xs text-emerald-600 truncate">+ {item.extras_details.map(e => e.name).join(", ")}</p>
                      )}
                      {item.notes && <p className="text-xs text-amber-600 italic truncate">"{item.notes}"</p>}
                      <p className="text-xs text-muted-foreground">{formatCurrency(item.price)} × {item.quantity}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateCartQuantity(item.id, -1)}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateCartQuantity(item.id, 1)}>
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeFromCart(item.id)}>
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Total & Actions */}
          {cart.length > 0 && (
            <div className="p-3 border-t space-y-2">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-primary">{formatCurrency(getCartTotal())}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={clearCart}>Clear</Button>
                <Button onClick={() => submitOrder("pay_at_cashier")} className="bg-green-600 hover:bg-green-700" data-testid="cashier-send-order-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Send to Kitchen
                </Button>
              </div>
            </div>
          )}

          {/* Recent Orders */}
          <div className="border-t max-h-48 overflow-y-auto">
            <div className="p-2 bg-gray-50 text-xs font-medium text-muted-foreground sticky top-0">
              Recent Orders (click to edit)
            </div>
            {recentOrders.map((order) => (
              <div 
                key={order.id}
                className="p-2 border-b hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                onClick={() => openEditOrder(order)}
              >
                <div>
                  <span className="font-mono font-bold">#{order.order_number.split("-")[1]}</span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {order.order_type === "eat_in" ? `T${order.table_number}` : "TK"}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={cn(
                    "text-xs",
                    order.status === "new" && "bg-blue-100 text-blue-700",
                    order.status === "preparing" && "bg-amber-100 text-amber-700",
                    order.status === "ready" && "bg-green-100 text-green-700"
                  )}>
                    {order.status}
                  </Badge>
                  <Edit className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Shared product customization modal */}
      <ProductCustomizationModal
        item={customizeItem}
        open={!!customizeItem}
        onClose={() => setCustomizeItem(null)}
        onAdd={addLineToCart}
        brandColor="#0EA5E9"
      />

      {/* Edit Order Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Order #{editingOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {editingOrder && (
            <div className="space-y-4">
              {/* Current Items */}
              <div className="space-y-2">
                <Label>Order Items</Label>
                {editingOrder.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(item.unit_price || item.total / item.quantity)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateEditingOrderItem(item.id, -1)}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateEditingOrderItem(item.id, 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Items */}
              <div className="space-y-2">
                <Label>Add Items</Label>
                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {menuItems.filter(i => i.is_available).map((item) => (
                    <Button
                      key={item.id}
                      variant="outline"
                      size="sm"
                      className="justify-start text-left h-auto py-2"
                      onClick={() => addItemToEditingOrder(item)}
                    >
                      <Plus className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>New Total</span>
                <span className="text-primary">
                  {formatCurrency(editingOrder.items.reduce((sum, i) => sum + (i.unit_price || i.total / i.quantity) * i.quantity, 0))}
                </span>
              </div>

              <Button onClick={saveEditedOrder} className="w-full">
                <Check className="w-4 h-4 mr-2" />
                Save & Notify Kitchen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashierOrders;
